import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Camera, MapPin, CheckCircle, XCircle, SignIn, SignOut, User, CalendarBlank } from '@phosphor-icons/react';
import axios from 'axios';
import { apiBase } from '@/utils/apiBase';

const API = apiBase();

export default function StaffPortalPage() {
  const [staff, setStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [mode, setMode] = useState('select'); // select, face, geofence, done
  const [message, setMessage] = useState('');
  const [msgType, setMsgType] = useState('info');
  const [videoStream, setVideoStream] = useState(null);
  const [location, setLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState('');
  const [leaveForm, setLeaveForm] = useState({ from_date: new Date().toISOString().slice(0, 10), to_date: '', reason: '' });
  const [token, setToken] = useState(localStorage.getItem('hc_token') || '');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loggedIn, setLoggedIn] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  // Login for portal
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/auth/login`, { email: loginEmail, password: loginPw });
      setToken(res.data.token);
      localStorage.setItem('hc_token', res.data.token);
      setLoggedIn(true);
    } catch (err) { setMessage('Login failed'); setMsgType('error'); }
  };

  // Fetch staff list
  const fetchStaff = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/staff`, { headers: { Authorization: `Bearer ${token}` } });
      setStaff(res.data);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { if (token) { setLoggedIn(true); fetchStaff(); } }, [token, fetchStaff]);

  // Camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 320, height: 240 } });
      setVideoStream(stream);
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch (err) { setMessage('Camera access denied'); setMsgType('error'); }
  };

  const stopCamera = () => {
    if (videoStream) { videoStream.getTracks().forEach(t => t.stop()); setVideoStream(null); }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return null;
    const canvas = canvasRef.current;
    canvas.width = 320;
    canvas.height = 240;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0, 320, 240);
    return canvas.toDataURL('image/jpeg', 0.7);
  };

  // Face Recognition Check-in
  const handleFaceCheckin = async () => {
    if (!selectedStaff) return;
    const photo = capturePhoto();
    if (!photo) { setMessage('Could not capture photo'); setMsgType('error'); return; }
    setMessage('Verifying face...'); setMsgType('info');
    try {
      const res = await axios.post(`${API}/staff/${selectedStaff.staff_id}/verify-face`, { selfie: photo }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.verified) {
        setMessage(`Face verified! ${selectedStaff.name} checked in. Confidence: ${res.data.confidence}%`);
        setMsgType('success');
        setMode('done');
      } else {
        setMessage(`Face not recognized. ${res.data.error || `Confidence: ${res.data.confidence}%`}`);
        setMsgType('error');
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Verification failed');
      setMsgType('error');
    }
    stopCamera();
  };

  // Register Face
  const handleRegisterFace = async () => {
    if (!selectedStaff) return;
    const photo = capturePhoto();
    if (!photo) { setMessage('Could not capture photo'); setMsgType('error'); return; }
    try {
      await axios.post(`${API}/staff/${selectedStaff.staff_id}/register-face`, { face_image: photo }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage(`Face registered for ${selectedStaff.name}!`);
      setMsgType('success');
      stopCamera();
      fetchStaff();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Registration failed');
      setMsgType('error');
    }
  };

  // Geofence Check-in
  const handleGeofenceCheckin = async (action = 'check_in') => {
    if (!selectedStaff) return;
    setGeoStatus('Getting location...');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setLocation({ lat: latitude, lng: longitude });
        setGeoStatus('Checking geofence...');
        try {
          const res = await axios.post(`${API}/staff/${selectedStaff.staff_id}/geofence-checkin`,
            { latitude, longitude, action },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.data.success) {
            setMessage(`${res.data.action === 'checked_in' ? 'Checked in' : 'Checked out'} via geofence! Distance: ${res.data.distance}m`);
            setMsgType('success');
            setMode('done');
          } else {
            setMessage(`${res.data.action}: ${res.data.within_fence ? 'Already checked in' : `Outside geofence (${res.data.distance}m away, radius: ${res.data.radius}m)`}`);
            setMsgType(res.data.action === 'already_checked_in' ? 'info' : 'error');
          }
        } catch (err) {
          setMessage(err.response?.data?.detail || 'Geofence check failed');
          setMsgType('error');
        }
        setGeoStatus('');
      },
      (err) => { setGeoStatus(''); setMessage('Location access denied. Enable GPS.'); setMsgType('error'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Manual check-in
  const handleManualCheckin = async () => {
    if (!selectedStaff) return;
    try {
      await axios.post(`${API}/staff/${selectedStaff.staff_id}/check-in`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMessage(`${selectedStaff.name} checked in manually!`); setMsgType('success'); setMode('done');
    } catch (err) { setMessage(err.response?.data?.detail || 'Check-in failed'); setMsgType('error'); }
  };

  const handleManualCheckout = async () => {
    if (!selectedStaff) return;
    try {
      await axios.post(`${API}/staff/${selectedStaff.staff_id}/check-out`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setMessage(`${selectedStaff.name} checked out!`); setMsgType('success'); setMode('done');
    } catch (err) { setMessage(err.response?.data?.detail || 'Check-out failed'); setMsgType('error'); }
  };

  const handleLeaveRequest = async () => {
    if (!selectedStaff || !leaveForm.from_date) return;
    try {
      await axios.post(`${API}/staff/${selectedStaff.staff_id}/leave-request`, leaveForm, { headers: { Authorization: `Bearer ${token}` } });
      setMessage(`Leave request sent for ${selectedStaff.name} — waiting for approval`); setMsgType('success'); setMode('done');
      setLeaveForm({ from_date: new Date().toISOString().slice(0, 10), to_date: '', reason: '' });
    } catch (err) { setMessage(err.response?.data?.detail || 'Leave request failed'); setMsgType('error'); }
  };

  useEffect(() => { return () => stopCamera(); }, []);

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="staff-portal-login">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><User size={32} className="text-primary" /></div>
            <h1 className="font-heading text-2xl font-bold">Staff Portal</h1>
            <p className="text-sm text-muted-foreground mt-1">Check-in with face or location</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="admin@homecommand.local" required data-testid="portal-email"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            <input type="password" value={loginPw} onChange={e => setLoginPw(e.target.value)} placeholder="Password" required data-testid="portal-password"
              className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            <button type="submit" data-testid="portal-login-btn" className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Sign In</button>
          </form>
          {message && <p className={`text-sm mt-4 text-center ${msgType === 'error' ? 'text-destructive' : 'text-primary'}`}>{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8 max-w-2xl mx-auto" data-testid="staff-portal-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-2xl font-bold">Staff Portal</h1>
        <p className="text-sm text-muted-foreground">Face Recognition & Geofence Attendance</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 text-sm font-medium mb-6 ${msgType === 'success' ? 'bg-green-500/10 text-green-600 border border-green-500/20' : msgType === 'error' ? 'bg-red-500/10 text-red-500 border border-red-500/20' : 'bg-blue-500/10 text-blue-500 border border-blue-500/20'}`} data-testid="portal-message">
          {message}
        </div>
      )}

      {mode === 'select' && (
        <div className="space-y-4">
          <p className="overline">Select Staff Member</p>
          <div className="grid grid-cols-2 gap-3">
            {staff.map(s => (
              <button key={s.staff_id} onClick={() => { setSelectedStaff(s); setMode('action'); setMessage(''); }}
                data-testid={`portal-staff-${s.staff_id}`}
                className="glass-card p-4 text-left card-hover">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold mb-2">{s.name?.[0]}</div>
                <p className="font-medium text-sm">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.role}</p>
                {s.face_registered && <span className="text-xs text-green-500 flex items-center gap-1 mt-1"><CheckCircle size={12} /> Face registered</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {mode === 'action' && selectedStaff && (
        <div className="space-y-4">
          <div className="text-center mb-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xl mx-auto mb-2">{selectedStaff.name?.[0]}</div>
            <p className="font-semibold">{selectedStaff.name}</p>
            <p className="text-sm text-muted-foreground">{selectedStaff.role}</p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <button onClick={() => { setMode('face'); startCamera(); }} data-testid="face-checkin-btn"
              className="glass-card p-4 text-left card-hover flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center"><Camera size={24} className="text-accent" /></div>
              <div><p className="font-medium text-sm">Face Recognition</p><p className="text-xs text-muted-foreground">{selectedStaff.face_registered ? 'Verify face to check-in' : 'Register face first'}</p></div>
            </button>
            <button onClick={() => { setMode('geofence'); handleGeofenceCheckin('check_in'); }} data-testid="geo-checkin-btn"
              className="glass-card p-4 text-left card-hover flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center"><MapPin size={24} className="text-primary" /></div>
              <div><p className="font-medium text-sm">Geofence Check-in</p><p className="text-xs text-muted-foreground">Auto check-in if near home</p></div>
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleManualCheckin} data-testid="manual-checkin-btn" className="rounded-xl border border-border bg-card p-3 text-center card-hover">
                <SignIn size={20} className="mx-auto text-green-600 mb-1" /><p className="text-xs font-medium">Manual Check-in</p>
              </button>
              <button onClick={handleManualCheckout} data-testid="manual-checkout-btn" className="rounded-xl border border-border bg-card p-3 text-center card-hover">
                <SignOut size={20} className="mx-auto text-red-500 mb-1" /><p className="text-xs font-medium">Manual Check-out</p>
              </button>
            </div>
            <button onClick={() => setMode('leave')} data-testid="request-leave-btn"
              className="glass-card p-4 text-left card-hover flex items-center gap-4 w-full">
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center"><CalendarBlank size={24} className="text-blue-500" /></div>
              <div><p className="font-medium text-sm">Request Leave / छुट्टी माँगें</p><p className="text-xs text-muted-foreground">Ask for a day off — owner will approve</p></div>
            </button>
          </div>
          <button onClick={() => { setMode('select'); setSelectedStaff(null); setMessage(''); stopCamera(); }} className="w-full py-2 text-sm text-muted-foreground hover:underline">Back to staff list</button>
        </div>
      )}

      {mode === 'face' && (
        <div className="space-y-4">
          <div className="glass-card p-4 overflow-hidden">
            <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-xl bg-black aspect-video object-cover" />
            <canvas ref={canvasRef} className="hidden" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {selectedStaff?.face_registered ? (
              <button onClick={handleFaceCheckin} data-testid="verify-face-btn" className="col-span-2 py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm flex items-center justify-center gap-2">
                <Camera size={18} /> Verify & Check-in
              </button>
            ) : (
              <button onClick={handleRegisterFace} data-testid="register-face-btn" className="col-span-2 py-3 rounded-full bg-accent text-accent-foreground font-semibold text-sm flex items-center justify-center gap-2">
                <Camera size={18} /> Register Face
              </button>
            )}
          </div>
          <button onClick={() => { setMode('action'); stopCamera(); setMessage(''); }} className="w-full py-2 text-sm text-muted-foreground hover:underline">Back</button>
        </div>
      )}

      {mode === 'geofence' && (
        <div className="text-center py-8">
          {geoStatus && <><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-sm text-muted-foreground">{geoStatus}</p></>}
          {location && <p className="text-xs text-muted-foreground mt-2">Your location: {location.lat.toFixed(5)}, {location.lng.toFixed(5)}</p>}
          <button onClick={() => { setMode('action'); setMessage(''); }} className="mt-4 py-2 text-sm text-muted-foreground hover:underline">Back</button>
        </div>
      )}

      {mode === 'leave' && selectedStaff && (
        <div className="space-y-4" data-testid="leave-request-form">
          <p className="overline">Leave Request — {selectedStaff.name}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">From / से</label>
              <input type="date" value={leaveForm.from_date} onChange={e => setLeaveForm({ ...leaveForm, from_date: e.target.value })} data-testid="leave-from-input"
                className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">To / तक</label>
              <input type="date" value={leaveForm.to_date} onChange={e => setLeaveForm({ ...leaveForm, to_date: e.target.value })} data-testid="leave-to-input"
                className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
            </div>
          </div>
          <input type="text" value={leaveForm.reason} onChange={e => setLeaveForm({ ...leaveForm, reason: e.target.value })} placeholder="Reason / कारण (optional)" data-testid="leave-reason-input"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
          <button onClick={handleLeaveRequest} data-testid="submit-leave-btn"
            className="w-full py-3 rounded-full bg-[#FF6B35] text-white btn-glow font-semibold text-sm">Submit Request / भेजें</button>
          <button onClick={() => { setMode('action'); setMessage(''); }} className="w-full py-2 text-sm text-muted-foreground hover:underline">Back</button>
        </div>
      )}

      {mode === 'done' && (
        <div className="text-center py-8">
          <CheckCircle size={48} weight="fill" className="text-green-500 mx-auto mb-4" />
          <p className="font-medium mb-4">Action completed!</p>
          <button onClick={() => { setMode('select'); setSelectedStaff(null); setMessage(''); }} data-testid="portal-reset-btn" className="px-6 py-2 rounded-full bg-[#FF6B35] text-white btn-glow text-sm font-semibold">Start Over</button>
        </div>
      )}
    </div>
  );
}
